import './style.css';
import { useEffect, useState } from 'react';
import {
  createModal,
  LoadingIndicator,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import { ConfigureDeviceModal, EditView } from '../manage-devices';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Link, useLocation } from 'react-router-dom';
import useAlarmsStore from 'store/alarms';
import useDevicesStore from 'store/devices';
import useDeviceTypesStore from 'store/device-types';
import useUsersStore from 'store/users';
import useRolesStore from 'store/roles';
import DevicesMap from '../devices-map';
import Actions, { Action } from '../actions';
import {
  getAllChildDevicesByType,
  getConfigItem
} from 'util/device';
import StatusIcon from 'components/status-icon';
import Gauge from 'components/circuit-gauges';
import CircuitTable from 'components/circuit-table';
import CircuitsGroupChart from 'components/charts/circuits-group-chart';


function DuoView({device}) {
  const location = useLocation();
  const [alarms, setAlarms] = useState([]);
  const [detail, setDetail] = useState();
  const [status, setStatus] = useState();
  const [loading, setLoading] = useState(true);
  const [alarmsStore] = useAlarmsStore();
  const [devicesStore, devicesStoreActions] = useDevicesStore();
  const [deviceTypesStore] = useDeviceTypesStore();
  const [, usersStoreActions] = useUsersStore();
  const [rolesStore] = useRolesStore();
  const [editMode, setEditMode] = useState(location.hash == '#network-settings');
  const unassigned = device.model.hasOwnProperty('assigned') && device.model.assigned === false;

  const controller = getAllChildDevicesByType(device.model, 'Controller')[0];
  const controllerType = deviceTypesStore.types.find(t => t.id == controller?.deviceModel);

  const circuits = getAllChildDevicesByType(device.model, 'Circuit');
  const [circuit1Detail, setCircuit1Detail] = useState();
  const [circuit1Status, setCircuit1Status] = useState();
  const [circuit2Detail, setCircuit2Detail] = useState();
  const [circuit2Status, setCircuit2Status] = useState();

  useEffect(() => {
    setEditMode(location.hash == '#network-settings');
  }, [location]);

  useEffect(() => {
    setLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((detail) => {
      setDetail(detail);
      setLoading(false);
    });

    devicesStoreActions.fetchStatus(device.model.id).then((status) => {
      setStatus(status);
    });

    if (circuits[0]) {
      devicesStoreActions.fetchDetail(circuits[0]?.id).then((detail) => {
        setCircuit1Detail(detail);
      });

      devicesStoreActions.fetchStatus(circuits[0]?.id).then((status) => {
        setCircuit1Status(status);
      });
    }

    if (circuits[1]) {
      devicesStoreActions.fetchDetail(circuits[1]?.id).then((detail) => {
        setCircuit2Detail(detail);
      });

      devicesStoreActions.fetchStatus(circuits[1]?.id).then((status) => {
        setCircuit2Status(status);
      });
    }
  }, [device]);

  useEffect(() => {
    if (!alarmsStore.alarms.length) {
      return;
    }

    // Check for alarm status on this device
    setAlarms(alarmsStore.alarms.filter(alarm => {
      if (!alarm.deviceId) return false;
      // If alarm is on this device add it
      if (alarm.deviceId == device.model.id) return true;
      // If this device is in the path of the device with the alarm add it
      const path = devicesStore.shallowDevices.get(alarm.deviceId)?.path.map(d => d.id);
      if (!path) return false;
      return path.includes(device.model.id);
    }));
  }, [alarmsStore]);

  const [settingsLoading, setSettingsLoading] = useState(false);
  function editSettings() {
    setSettingsLoading(true);
    devicesStoreActions.fetchDetail(device.model.id).then((res) => {
      setSettingsLoading(false);
      setDetail(res);
      const editModal = createModal(<ConfigureDeviceModal device={res} />);
      editModal.open()
        .then((res) => {
          // Handle side-effects
          devicesStoreActions.forceUpdate();
        })
        .catch(() => {/* no-op */});
    });
  }

  const [controllerSettingsLoading, setControllerSettingsLoading] = useState(false);
  function editControllerSettings() {
    if (!controller?.id) return;
    setControllerSettingsLoading(true);
    devicesStoreActions.fetchDetail(controller.id).then((res) => {
      setControllerSettingsLoading(false);
      const editModal = createModal(<ConfigureDeviceModal device={res} />);
      editModal.open()
        .then((res) => {
          // Handle side-effects
          devicesStoreActions.forceUpdate();
        })
        .catch(() => {/* no-op */});
    });
  }

  if (editMode) return (
    <EditView device={device} />
  );

  return (
    <>
    <header>
      <h2>
        {loading ?
          <LoadingIndicator /> :
          <StatusIcon device={device} alarms={alarms} />
        }
        {device.model.name}
      </h2>
      <ul className="device-controls">
        {usersStoreActions.checkAccess('CanChangeConfiguration', rolesStore.roles) ? <>
          <li>
            <button
              className={`settings ${settingsLoading ? 'processing' : ''}`}
              onClick={editSettings}
              data-tooltip-content={t`Duo Settings`}
              data-tooltip-id="control-tooltip"
              disabled={loading || settingsLoading}
            >
              <Trans>Settings</Trans>
            </button>
          </li>
          <li>
            <button
              className={`controller settings ${controllerSettingsLoading ? 'processing' : ''}`}
              onClick={editControllerSettings}
              data-tooltip-content={t`Controller Settings`}
              data-tooltip-id="control-tooltip"
              disabled={loading || controllerSettingsLoading}
            >
              <Trans>Controller Settings</Trans>
            </button>
          </li>
          <li>
            <Link
              to={'#network-settings'}
              className="button network"
              data-tooltip-content={t`Network Settings`}
              data-tooltip-id="control-tooltip"
              onClick={() => setEditMode(true)}
            >
              <Trans>Network Settings</Trans>
            </Link>
          </li>
        </> : <></>}
        <Actions wrapper={true} device={device.model} />
        {controllerType?.actions?.find(action => action.name === 'Ping') ? <li>
          <Action
            device={device.model}
            action={controllerType.actions.find(action => action.name === 'Ping')}
          />
        </li> : null}
      </ul>
      <Tooltip id="control-tooltip" place="bottom-end" />
    </header>
    <section>
      <div className="overview">
        <ul>
          <li>
            <strong><Trans>Status</Trans></strong>: {unassigned ? t`Unassigned` : status?.commStatus}
            &nbsp;
          </li>
          {/* Hide firmware info from Virtual Bridges */}
          {device.model.deviceModel != 49 ? <>
          <li>
            <strong><Trans>Firmware Version</Trans></strong>:&nbsp;
            {getConfigItem(detail, 'FirmwareVersion')?.value}
          </li>
          <li>
            <strong><Trans>Firmware Updated</Trans></strong>:&nbsp;
            {new Date(getConfigItem(detail, 'FirmwareDate')?.value).toLocaleString()}
          </li>
          </> : null}
        </ul>
        <DevicesMap device={device} />
      </div>
      {circuit1Detail && circuit1Status ? <div className="circuit-gauge-set">
        <Gauge
          size={120}
          model={
            {
              low: +getConfigItem(circuit1Detail, 'CurrentLowAlarm')?.value,
              high: +getConfigItem(circuit1Detail, 'CurrentHighAlarm')?.value,
              trip: +getConfigItem(circuit1Detail, 'CurrentTripAlarm')?.value,
              current: circuit1Status.commStatus != 'ok' ? null : circuit1Status.current
            }
          }
          unit={'A'}
          className="current"
        />
        <Gauge
          model={
            {
              low: +getConfigItem(circuit1Detail, 'TemperatureLowAlarm')?.value,
              high: +getConfigItem(circuit1Detail, 'TemperatureHighAlarm')?.value,
              trip: +getConfigItem(circuit1Detail, 'TemperatureHighTrip')?.value,
              maintain: +getConfigItem(circuit1Detail, 'TemperatureMaintain')?.value,
              max: +getConfigItem(circuit1Detail, 'TemperatureMaximum')?.value,
current: circuit1Status.commStatus != 'ok' ? null : circuit1Status.temperature,
               power: circuit1Status.commStatus != 'ok' ? null : circuit1Status.powerDelivery
            }
          }
          unit={`°${circuit1Status.temperatureUnit || '–'}`}
          className="temperature"
        />
        <Gauge
          size={120}
          model={
            {
              low: +getConfigItem(circuit1Detail, 'GroundCurrentLowAlarm')?.value,
              high: +getConfigItem(circuit1Detail, 'GroundCurrentHighAlarm')?.value,
              trip: +getConfigItem(circuit1Detail, 'GroundCurrentTripAlarm')?.value,
              current: circuit1Status.commStatus != 'ok' ? null : circuit1Status.groundCurrent
            }
          }
          unit={'mA'}
          className="ground-current"
        />
      </div> : null}
      {circuit2Detail && circuit2Status ?  <div className="circuit-gauge-set">
        <Gauge
          size={120}
          model={
            {
              low: +getConfigItem(circuit2Detail, 'CurrentLowAlarm')?.value,
              high: +getConfigItem(circuit2Detail, 'CurrentHighAlarm')?.value,
              trip: +getConfigItem(circuit2Detail, 'CurrentTripAlarm')?.value,
              current: circuit2Status.commStatus != 'ok' ? null : circuit2Status.current
            }
          }
          unit={'A'}
          className="current"
        />
        <Gauge
          model={
            {
              low: +getConfigItem(circuit2Detail, 'TemperatureLowAlarm')?.value,
              high: +getConfigItem(circuit2Detail, 'TemperatureHighAlarm')?.value,
              trip: +getConfigItem(circuit2Detail, 'TemperatureHighTrip')?.value,
              maintain: +getConfigItem(circuit2Detail, 'TemperatureMaintain')?.value,
              max: +getConfigItem(circuit2Detail, 'TemperatureMaximum')?.value,
current: circuit2Status.commStatus != 'ok' ? null : circuit2Status.temperature,
               power: circuit2Status.commStatus != 'ok' ? null : circuit2Status.powerDelivery
            }
          }
          unit={`°${circuit2Status.temperatureUnit || '–'}`}
          className="temperature"
        />
        <Gauge
          size={120}
          model={
            {
              low: +getConfigItem(circuit2Detail, 'GroundCurrentLowAlarm')?.value,
              high: +getConfigItem(circuit2Detail, 'GroundCurrentHighAlarm')?.value,
              trip: +getConfigItem(circuit2Detail, 'GroundCurrentTripAlarm')?.value,
              current: circuit2Status.commStatus != 'ok' ? null : circuit2Status.groundCurrent
            }
          }
          unit={'mA'}
          className="ground-current"
        />
      </div> : null}
      <CircuitTable device={{model: controller}} />
      <br />
      <CircuitsGroupChart
        circuitIds={circuits.map(c => c.id)}
      />
    </section>
    </>
  )
}

export default DuoView;
